import { getToken } from "../../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../../config/api";

/**
 * Zoho Books Automation Accounts → Mail box API.
 *
 * Mirrors cronApi/provisioningApi conventions (Bearer token, APIError, POST
 * endpoints). Reads come from the backend's cache, so the list and the message
 * viewer are cheap; only syncMail() talks to Gmail.
 */

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

/** The Google Cloud OAuth app. The secret is never returned — only a flag. */
export interface OAuthClientDTO {
  label: string | null;
  client_id: string;
  redirect_uri: string;
  app_return_url: string | null;
  scopes: string | null;
  has_client_secret: boolean;
  updated_at: string | null;
}

export interface OAuthClientInput {
  client_id: string;
  /** Leave blank on an update to keep the stored secret. */
  client_secret?: string;
  redirect_uri: string;
  app_return_url?: string;
  label?: string;
  scopes?: string;
}

export interface MailAccountDTO {
  public_id: string;
  label: string | null;
  email_address: string;
  provider: string;
  auth_type: string;
  purpose: string;
  status: "connected" | "disconnected" | "error";
  granted_scopes: string | null;
  /** Start of the daily fetch window, e.g. "21:00:00" — the "after 9pm" rule. */
  fetch_after_time: string;
  fetch_timezone: string;
  gmail_query: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  is_default: boolean;
  is_active: boolean;
  has_refresh_token: boolean;
  token_expires_at: string | null;
  created_at: string | null;
}

export interface MailMessageSummaryDTO {
  id: number;
  provider_message_id: string;
  provider_thread_id: string | null;
  /** UTC ISO-8601 — render with toLocaleString() for the viewer's clock. */
  received_at: string | null;
  from_name: string | null;
  from_email: string | null;
  /** Delivered-To header — which zoho-*@ address the mail actually arrived at. */
  delivered_to: string | null;
  subject: string | null;
  snippet: string | null;
  is_unread: boolean;
  has_attachments: boolean;
  size_estimate: number | null;
  processed_status: string;
}

export interface MailAttachmentDTO {
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
}

export interface MailMessageDTO extends MailMessageSummaryDTO {
  to_emails: string | null;
  cc_emails: string | null;
  reply_to: string | null;
  label_ids: string[];
  body_text: string | null;
  body_html: string | null;
  attachments: MailAttachmentDTO[];
}

/** The window actually applied, as ISO strings carrying the mailbox's offset. */
export interface MailWindowDTO {
  start: string;
  end: string;
  timezone: string;
}

export interface SyncResultDTO {
  matched: number;
  fetched: number;
  new: number;
  skipped: number;
  failed: number;
  query: string;
  window_start: string;
  window_end: string;
  duration_ms: number;
  truncated: boolean;
  error: string | null;
}

export interface SyncRunDTO {
  id: number;
  trigger_source: string;
  query_used: string | null;
  window_start: string | null;
  window_end: string | null;
  status: string;
  fetched_count: number;
  new_count: number;
  duration_ms: number | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export type SettingsResponseDTO = Envelope<{
  oauth_client: OAuthClientDTO | null;
  /** Derived from the host this request arrived on, not from a config constant. */
  suggested_redirect_uri: string;
  default_scopes: string;
  /** False when the stored redirect URI points at a different host than we're on. */
  redirect_uri_matches_host: boolean;
}>;

export type AccountsResponseDTO = Envelope<{
  accounts: MailAccountDTO[];
  default_public_id: string | null;
  oauth_configured: boolean;
}>;

export type ConnectResponseDTO = Envelope<{ url: string; state: string }>;

export type MessagesResponseDTO = Envelope<{
  messages: MailMessageSummaryDTO[];
  total: number;
  window: MailWindowDTO;
  account: MailAccountDTO;
}>;

export type MessageResponseDTO = Envelope<{ message: MailMessageDTO }>;

export type SyncResponseDTO = Envelope<{
  sync: SyncResultDTO;
  account: MailAccountDTO;
}>;

export type SyncRunsResponseDTO = Envelope<{ runs: SyncRunDTO[] }>;

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new APIError(
        payload.message || `HTTP error! status: ${response.status}`,
        response.status,
        payload
      );
    }

    return payload as T;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${error instanceof Error ? error.message : "Unknown error"}`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
}

/** Times are sent as "YYYY-MM-DDTHH:mm" and read in the mailbox's timezone. */
export interface MailWindowInput {
  public_id?: string | null;
  from?: string | null;
  to?: string | null;
  limit?: number;
  offset?: number;
}

export const getBotMailSettings = async (): Promise<SettingsResponseDTO> =>
  postJson<SettingsResponseDTO>("/api/bot/mail/settings/get", {});

export const saveBotMailSettings = async (
  input: OAuthClientInput
): Promise<Envelope<{ oauth_client: OAuthClientDTO }>> =>
  postJson("/api/bot/mail/settings/save", input);

export const listMailAccounts = async (): Promise<AccountsResponseDTO> =>
  postJson<AccountsResponseDTO>("/api/bot/mail/accounts/list", {});

/**
 * Ask for a Google consent URL. The caller does the redirect, so the admin can
 * see where they're going before they get there.
 */
export const connectMailAccount = async (input: {
  email?: string;
  label?: string;
  public_id?: string;
}): Promise<ConnectResponseDTO> =>
  postJson<ConnectResponseDTO>("/api/bot/mail/accounts/connect", input);

export const updateMailAccount = async (input: {
  public_id: string;
  label?: string;
  gmail_query?: string | null;
  fetch_after_time?: string;
  fetch_timezone?: string;
  is_default?: boolean;
  is_active?: boolean;
}): Promise<Envelope<{ account: MailAccountDTO }>> =>
  postJson("/api/bot/mail/accounts/update", input);

export const disconnectMailAccount = async (
  publicId: string
): Promise<Envelope<{ account: MailAccountDTO }>> =>
  postJson("/api/bot/mail/accounts/disconnect", { public_id: publicId });

export const listMail = async (input: MailWindowInput): Promise<MessagesResponseDTO> =>
  postJson<MessagesResponseDTO>("/api/bot/mail/messages/list", input);

export const getMail = async (
  id: number | string,
  publicId?: string | null
): Promise<MessageResponseDTO> =>
  postJson<MessageResponseDTO>("/api/bot/mail/messages/get", {
    id: String(id),
    public_id: publicId ?? undefined,
  });

export const syncMail = async (input: MailWindowInput): Promise<SyncResponseDTO> =>
  postJson<SyncResponseDTO>("/api/bot/mail/sync", input);

export const listSyncRuns = async (
  publicId?: string | null,
  limit = 10
): Promise<SyncRunsResponseDTO> =>
  postJson<SyncRunsResponseDTO>("/api/bot/mail/sync-runs", {
    public_id: publicId ?? undefined,
    limit,
  });

export { APIError as BotMailAPIError };
